import type {
  RevisionDTO,
  RevisionUpdateDTO,
} from '@conversed/rest/src/contracts';
import type { ConversationKeywordDTO } from '@conversed/rest/src/contracts/conversation-keywords/conversation-keywords.dto';
import type { DataSourceDetailsDTO } from '@conversed/rest/src/contracts/data-sources/data-sources.dto';
import type { DataSourceFolderWithSubfoldersDTO } from '@conversed/rest/src/contracts/data-sources-folders/data-sources-folders.schema';
import type {
  TConversationDetailsSchema,
  TLLMUsagesHistoryResponse,
} from '@conversed/rest/src/contracts/llm-usages/llm-usages.contract';
import type { TPlatformReportingResponse } from '@conversed/rest/src/contracts/reporting/reporting.contract';
import type { SubflowTemplateDTO } from '@conversed/rest/src/contracts/subflow-templates/subflow-templates.dto';
import type { TemplateResponseDTO } from '@conversed/rest/src/contracts/template-response/template-response.dto';
import type { VoiceAIAgentDataDTO } from '@conversed/rest/src/contracts/voice-ai/voice-ai.dto';
import { VoiceAIAgentReason } from '@conversed/rest/src/contracts/voice-ai/voice-ai.enums';
import type {
  CompanyMemberDTO,
  CompanyPackagesDTO,
} from '@conversed/shared/src/central/dto/company';
import type { TBlockConfig } from '@conversed/shared/src/central/types/block.type';
import type { TActivityChatState } from '@conversed/shared/src/central/types/chat.types';
import type {
  PlatformFeaturesMetadata,
  PlatformFeatureType,
} from '@conversed/shared/src/central/types/platform-features.type';
import type { PreviewConfigsDTO } from '@conversed/shared/src/central/types/preview';
import type { TRole } from '@conversed/shared/src/central/types/role.types';
import {
  addValueToList,
  pushUniques,
  removeObjectValuesFromList,
  removeValueFromList,
  replaceValueInList,
  replaceValuesInList,
} from '@conversed/shared/src/helpers/data.helpers';
import type { THandoverSetting } from '@conversed/shared/src/internal/models/app-config.type';
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { Actions } from '../../constants/actionTypes';
import type { TCurrentUser } from '../../interfaces/IAuth';
import type { TChatHistory } from '../../interfaces/IMessage';
import type { TActivityLogStateInner } from '../../interfaces/redux/TActivityLogState';
import type { TAuthorization } from '../../interfaces/redux/TAuthState';
import type {
  TChatNotification,
  TChatStateInner,
} from '../../interfaces/redux/TChatState';
import type { TColorStateInner } from '../../interfaces/redux/TColorState';
import type { TDesignerStateInner } from '../../interfaces/redux/TDesignerState';
import type { TGDPRStateInner } from '../../interfaces/redux/TGDPRState';
import type { TLoginInner } from '../../interfaces/redux/TLoginState';
import type {
  TChatPlugin,
  TCompanySettingsState,
  TDebugConfig,
  TExportConfig,
  TLink,
  TSettingsStateInner,
  TUpdateChatDate,
  TUserDataMappingCongig,
  TUserProfileField,
  TValidationConfig,
  TWebclient,
} from '../../interfaces/redux/TSettingsState';
import type { TUIStateInner } from '../../interfaces/redux/TUIState';
import type { TUserStateInner } from '../../interfaces/redux/TUserState';
import type { TAssignee, TConversation } from '../../interfaces/TChat';
import type { TGDPRUser } from '../../interfaces/TGDPR';
import type { TReduxState } from '../../interfaces/TReduxState';
import type { TS3File } from '../../interfaces/TS3File';
import { initialState } from '../initialState';

type Action<T = undefined> = (
  state: TReduxState,
  action: PayloadAction<T>,
) => void;

// * `reason` travels with the list rather than in its own action, so a writer that
// * has no reason to report clears a stale one instead of leaving it behind. The
// * reducer overwrites both fields rather than merging, unlike most actions here -
// * a merge would silently keep a stale reason when a writer omits it.
type TSetVoiceAIAgentsPayload = Pick<VoiceAIAgentDataDTO, 'agents' | 'reason'>;

const setLogIn: Action<Partial<TLoginInner>> = (state, action) => {
  state.login = { ...state.login, ...action.payload };
};

const setChatState: Action<Partial<TChatStateInner>> = (state, action) => {
  state.chat = { ...state.chat, ...action.payload };
};

const setUIState: Action<Partial<TUIStateInner>> = (state, action) => {
  state.ui = { ...state.ui, ...action.payload };
};

const setUserState: Action<Partial<TUserStateInner>> = (state, action) => {
  state.user = { ...state.user, ...action.payload };
};

const setSettingsState: Action<Partial<TSettingsStateInner>> = (
  state,
  action,
) => {
  state.settings = { ...state.settings, ...action.payload };
};

const setDesignerState: Action<Partial<TDesignerStateInner>> = (
  state,
  action,
) => {
  state.designer = { ...state.designer, ...action.payload };
};

const setColorState: Action<Partial<TColorStateInner>> = (state, action) => {
  state.colors = { ...state.colors, ...action.payload };
};

const setConversationAssigneeList: Action<TAssignee[]> = (state, action) => {
  if (!state.chat.selectedConversation) {
    return state;
  }

  state.chat.selectedConversation = {
    ...state.chat.selectedConversation,
    assigneeList: action.payload,
  };
};

const setConversationMessages: Action<TChatHistory[]> = (state, action) => {
  if (!state.chat.selectedConversation) {
    return state;
  }

  state.chat.selectedConversation = {
    ...state.chat.selectedConversation,
    messages: action.payload,
  };
};

const setSelectedConversation: Action<Partial<TConversation>> = (
  state,
  action,
) => {
  if (!state.chat.selectedConversation) {
    return state;
  }

  state.chat.selectedConversation = {
    ...state.chat.selectedConversation,
    ...action.payload,
  };
};

const setCompanySettings: Action<Partial<TCompanySettingsState>> = (
  state,
  action,
) => {
  state.settings.company = { ...state.settings.company, ...action.payload };
};

const addCompanyMember: Action<CompanyMemberDTO> = (state, action) => {
  const { payload: member } = action;

  state.settings.company = {
    ...state.settings.company,
    members: replaceValueInList(
      member,
      state.settings.company?.members ?? [],
      (currentMember) => currentMember.id === member.id,
    ),
  };
};

const updateCompanyPackages: Action<CompanyPackagesDTO> = (state, action) => {
  const { payload: packages } = action;
  const selectedCompanyId = state.user?.currentUser?.selectedCompanyId;

  if (
    !selectedCompanyId ||
    !Array.isArray(state.user?.currentUser?.companies)
  ) {
    return state;
  }

  state.user.currentUser.companies = state.user.currentUser.companies.map(
    (company) =>
      company.identifier === selectedCompanyId
        ? {
            ...company,
            packages: {
              ...company.packages,
              ...packages,
            },
          }
        : company,
  );
};

const updateCompanyPlatformFeatures: Action<PlatformFeatureType[]> = (
  state,
  action,
) => {
  const { payload: platformFeatures } = action;

  if (!state.settings.company?.generalSettings?.name) {
    return;
  }

  // Update generalSettings
  state.settings.company.generalSettings = {
    ...state.settings.company.generalSettings,
    platformFeatures: [...platformFeatures],
  };

  // Also update the selected company's platformFeatures
  const selectedCompanyId = state.user?.currentUser?.selectedCompanyId;

  if (selectedCompanyId && Array.isArray(state.user?.currentUser?.companies)) {
    state.user.currentUser.companies = state.user.currentUser.companies.map(
      (company) =>
        company.identifier === selectedCompanyId
          ? {
              ...company,
              platformFeatures: [...platformFeatures],
            }
          : company,
    );
  }
};

const updateCompanyPlatformFeaturesMetadata: Action<
  PlatformFeaturesMetadata
> = (state, action) => {
  if (!state.settings.company?.generalSettings?.name) {
    return;
  }

  state.settings.company.generalSettings = {
    ...state.settings.company.generalSettings,
    platformFeaturesMetadata: action.payload,
  };
};

const setAssignableRoles: Action<TRole[]> = (state, action) => {
  state.settings.company = {
    ...state.settings.company,
    assignableRoles: action.payload,
  };
};

const changePermission = (
  state: TReduxState,
  role: TRole,
  permissionList: string[],
) => {
  state.settings.roles = replaceValueInList(
    { ...role, permissions: permissionList },
    state.settings.roles,
    (roleToReplace) => roleToReplace.id === role.id,
  );
};

const togglePermissionForRole: Action<TAuthorization> = (state, action) => {
  const { payload: authorization } = action;
  const { permission, role } = authorization;
  const roleHasPermission = role.permissions.find(
    (roleInPermission) => roleInPermission === permission,
  );

  if (!roleHasPermission) {
    changePermission(state, role, addValueToList(permission, role.permissions));
    return state;
  }

  changePermission(
    state,
    role,
    removeValueFromList(
      (permissionToRemove) => permissionToRemove === permission,
      role.permissions,
    ),
  );
};

const setRoles: Action<TRole[]> = (state, action) => {
  state.settings.roles = action.payload;
};

const updateCurrentUser: Action<Partial<TCurrentUser>> = (state, action) => {
  if (!state.user.currentUser) {
    return state;
  }

  state.user.currentUser = {
    ...state.user.currentUser,
    ...action.payload,
  };
};

const getCreatedBlocks = (state: TReduxState, blocks: TBlockConfig[]) => {
  const existingBlocks = state.designer.blocks ?? [];
  const currentlyCreatedBlocks = state.designer.blockUpdates.created;

  const blocksToReplace = blocks.filter((block) => {
    const blockInExistingBlocks = existingBlocks.find(
      (existingBlock) => existingBlock.id === block.id,
    );
    const blockInCurrentlyCreated = currentlyCreatedBlocks.find(
      (createdBlock) => createdBlock.id === block.id,
    );

    return !blockInExistingBlocks || blockInCurrentlyCreated;
  });

  return replaceValuesInList(blocksToReplace, currentlyCreatedBlocks, 'id');
};

const getUpdatedBlocks = (state: TReduxState, blocks: TBlockConfig[]) => {
  const existingBlocks = state.designer.blocks ?? [];
  const currentlyCreatedBlocks = state.designer.blockUpdates.created;
  const currentlyUpdatedBlocks = state.designer.blockUpdates.updated;

  const blocksToReplace = blocks.filter((block) => {
    const blockInExistingBlocks = existingBlocks.find(
      (existingBlock) => existingBlock.id === block.id,
    );
    const blockInCurrentlyCreated = currentlyCreatedBlocks.find(
      (createdBlock) => createdBlock.id === block.id,
    );

    return blockInExistingBlocks || !blockInCurrentlyCreated;
  });

  return replaceValuesInList(blocksToReplace, currentlyUpdatedBlocks, 'id');
};

const updateBlocks: Action<TBlockConfig[]> = (state, action) => {
  const { payload } = action;

  const created = getCreatedBlocks(state, payload);
  const updated = getUpdatedBlocks(state, payload);
  const blocks = replaceValuesInList(
    payload,
    state.designer.blocks ?? [],
    'id',
  );

  state.designer = {
    ...state.designer,
    blockUpdates: {
      ...state.designer.blockUpdates,
      created,
      updated,
    },
    blocks,
  };
};

const deleteBlocks: Action<string[]> = (state, action) => {
  const { payload: blockIds } = action;

  const created = state.designer.blockUpdates.created.filter(
    (block) => !blockIds.includes(block.id),
  );
  const updated = state.designer.blockUpdates.updated.filter(
    (block) => !blockIds.includes(block.id),
  );
  const deleted = pushUniques(state.designer.blockUpdates.deleted, blockIds);
  const blocks = removeObjectValuesFromList(
    blockIds,
    state.designer.blocks ?? [],
    'id',
  );

  state.designer = {
    ...state.designer,
    blockUpdates: {
      ...state.designer.blockUpdates,
      created,
      updated,
      deleted,
    },
    blocks,
  };
};

const clearBlockUpdates: Action = (state) => {
  state.designer.blockUpdates = {
    updated: [],
    created: [],
    deleted: [],
    subFlowsCreated: [],
    subFlowsDeleted: [],
  };
};

const setLinkStatistics: Action<Partial<TLink>> = (state, action) => {
  const { payload: statistics } = action;
  state.settings.links = state.settings.links.map((link) =>
    link.shortLink === statistics.shortLink
      ? { ...link, totalClicksCount: statistics.totalClicksCount }
      : link,
  );
};

const setExportFiles: Action<{ key: string }[]> = (state, action) => {
  const { pathname } = window.location;
  const path = pathname.startsWith('/exports')
    ? decodeURIComponent(pathname).substring(1)
    : decodeURIComponent(pathname);

  state.exports.files = action.payload
    .map((file) => {
      const fullPathWithoutExports = file.key.startsWith('exports/')
        ? file.key.substring(8)
        : file.key;

      if (path === 'exports') {
        const withoutExports = file.key.startsWith('exports/')
          ? file.key.substring(8)
          : file.key;
        const pathParts = withoutExports.split('/');

        if (pathParts.length === 1) {
          const fileName = pathParts[0];
          return {
            fullPath: fullPathWithoutExports,
            key: fileName,
            fileName: fileName,
            isFile: fileName.includes('.'),
            navigationPath: fileName,
          };
        }
        const folderName = pathParts[0];
        return {
          fullPath: fullPathWithoutExports,
          key: folderName,
          fileName: folderName,
          isFile: false,
          navigationPath: folderName,
        };
      }

      // In Subdirectory
      const expectedPrefix = `${path}/`;
      if (!file.key.startsWith(expectedPrefix)) {
        return null;
      }

      const relativePath = file.key.substring(expectedPrefix.length);
      if (!relativePath) {
        return null;
      }

      const pathParts = relativePath.split('/');
      if (pathParts.length === 1) {
        const fileName = pathParts[0];
        return {
          fullPath: fullPathWithoutExports,
          key: fileName,
          fileName: fileName,
          isFile: fileName.includes('.'),
          navigationPath: `${path.substring(8)}/${fileName}`, // Full path for navigation
        };
      }

      const subfolderName = pathParts[0];
      const currentSubPath = path.substring(8); // Remove 'exports/'
      return {
        fullPath: fullPathWithoutExports,
        key: subfolderName,
        fileName: subfolderName,
        isFile: false,
        navigationPath: `${currentSubPath}/${subfolderName}`, // Full path for navigation
      };
    })
    .filter((file): file is TS3File => file !== null) // Remove null values
    .filter((file, index, array) => {
      // Remove duplicates (same folder shown multiple times)
      return array.findIndex((f) => f.key === file.key) === index;
    });
};

const setSelectedExportFile: Action<TS3File> = (state, action) => {
  state.exports.selectedFile = action.payload;
};

const removeExport: Action<string> = (state, action) => {
  state.exports.files = state.exports.files.filter(
    (file) => file.fullPath !== action.payload,
  );
};

const setExportSearchString: Action<string> = (state, action) => {
  state.exports.search = action.payload;
};

const setGDPRUsers: Action<Partial<TGDPRStateInner>> = (state, action) => {
  state.gdpr = {
    ...state.gdpr,
    ...action.payload,
  };
};

const removeGDPRUser: Action<string> = (state, action) => {
  const { payload: id } = action;

  state.gdpr.gdprUsers = removeValueFromList(
    (gdprUser) => id === gdprUser.userId,
    state.gdpr.gdprUsers,
  );
};

const toggleSelectedLanguage: Action = (state) => {
  const {
    designer: { blockLanguages, blockLanguage },
  } = state;

  const index = blockLanguages.findIndex((value) => value === blockLanguage);
  const nextLanguageIndex = blockLanguages.length - 1 === index ? 0 : index + 1;

  state.designer.blockLanguage = blockLanguages[nextLanguageIndex];
};

const setLanguages: Action<
  Partial<Pick<TDesignerStateInner, 'blockLanguage' | 'blockLanguages'>>
> = (state, action) => {
  state.designer = { ...state.designer, ...action.payload };
};

const setExportConfigStatus: Action<{ id: string; status: boolean }> = (
  state,
  action,
) => {
  const { id, status } = action.payload;
  state.settings.exportConfigs = state.settings.exportConfigs.map(
    (exportConfig) =>
      exportConfig.id === id
        ? { ...exportConfig, isEnabled: status }
        : exportConfig,
  );
};

const removeExportConfig: Action<string> = (state, action) => {
  state.settings.exportConfigs = state.settings.exportConfigs.filter(
    (exportConfig) => exportConfig.id !== action.payload,
  );
};

const addExportConfig: Action<TExportConfig> = (state, action) => {
  state.settings.exportConfigs = [
    ...state.settings.exportConfigs,
    action.payload,
  ];
};

const editExportConfig: Action<TExportConfig> = (state, action) => {
  state.settings.exportConfigs = replaceValueInList(
    action.payload,
    state.settings.exportConfigs,
    (exportConfig) => exportConfig.id === action.payload.id,
  );
};

const setSelectedExportConfig: Action<TExportConfig> = (state, action) => {
  state.settings.selectedExportConfig = action.payload;
};

const setSelectedGDPRUser: Action<TGDPRUser> = (state, action) => {
  state.gdpr.selectedUser = action.payload;
};

const setActivityLogs: Action<TActivityLogStateInner['logs']> = (
  state,
  action,
) => {
  state.activityLog.logs = action.payload;
};

const addChatNotifications: Action<TChatNotification[]> = (state, action) => {
  state.chat.notifications = [
    ...(state.chat.notifications ?? []),
    ...action.payload,
  ];
};

const clearChatNotifications: Action<string | undefined> = (state, action) => {
  if (!action.payload) {
    state.chat.notifications = [];
    return state;
  }

  state.chat.notifications =
    state.chat.notifications?.filter(
      (notification) => notification.user !== action.payload,
    ) ?? [];
};

const setDebugConfig: Action<TDebugConfig> = (state, action) => {
  state.settings.debugConfig = action.payload;
};

const setValidationConfig: Action<TValidationConfig> = (state, action) => {
  state.settings.validationConfig = action.payload;
};

const setAvailabilities: Action<THandoverSetting[]> = (state, action) => {
  state.settings.handover.availabilities = action.payload;
};

const setMFAForCompany: Action<boolean> = (state, action) => {
  return {
    ...state,
    settings: {
      ...state.settings,
      company: {
        ...state.settings.company,
        generalSettings: {
          ...state.settings.company?.generalSettings,
          mfaEnforced: action.payload,
        },
      },
    },
  };
};

const setSelectedDate: Action<string> = (state, action) => {
  state.settings.handover.selectedDate = action.payload;
};

const setUserDataMapping: Action<TUserDataMappingCongig> = (state, action) => {
  state.settings.userDataMapping = action.payload;
};

const setCalendarFilter: Action<string> = (state, action) => {
  return {
    ...state,
    settings: {
      ...state.settings,
      handover: {
        ...state.settings.handover,
        filter: action.payload,
      },
    },
  };
};

const setCalendarView: Action<'week' | 'month'> = (state, action) => {
  state.settings.handover.calendarView = action.payload;
};

const setTimezone: Action<string> = (state, action) => {
  state.settings.timezone = action.payload;
};

const setStreamingResponseConfig: Action<boolean> = (state, action) => {
  state.settings.useStreamingResponse = action.payload;
};

const setRestartPolicy: Action<string[]> = (state, action) => {
  state.settings.webclient.restartPolicyWhitelist = action.payload;
};

const setIsEnabledWebclientAccess: Action<boolean> = (state, action) => {
  state.settings.webclient.isEnabledWebclientAccess = action.payload;
};

const setWebclient: Action<TWebclient> = (state, action) => {
  state.settings.webclient = {
    ...state.settings.webclient,
    ...action.payload,
  };
};

const setUserProfileFields: Action<TUserProfileField[]> = (state, action) => {
  state.settings.messengerSettings.userProfileFields = action.payload;
};

const setChatPlugin: Action<TChatPlugin> = (state, action) => {
  state.settings.messengerSettings.chatPlugin = action.payload;
};

const setSubFlowToDisplay: Action<string> = (state, action) => {
  state.designer.flow.selectedSubFlow = action.payload;
};

const setSubFlowsForFlow: Action<string[]> = (state, action) => {
  state.designer.flow.subFlows = action.payload;
};

const addSubFlowForFlow: Action<string> = (state, action) => {
  if (state.designer.flow.subFlows.includes(action.payload)) {
    return;
  }

  state.designer.flow.subFlows.push(action.payload);
};

const removeSubFlowFromFlow: Action<string> = (state, action) => {
  const subFlowAtIndex = state.designer.flow.subFlows.indexOf(action.payload);
  if (subFlowAtIndex < 0) {
    return;
  }
  state.designer.flow.subFlows.splice(subFlowAtIndex, 1);
  state.designer.flow.selectedSubFlow = 'main';
};

const updateSubFlow: Action<{
  oldName: string;
  newName: string;
  navigateTo?: string;
}> = (state, action) => {
  const { oldName, newName } = action.payload;
  const index = state.designer.flow.subFlows.indexOf(oldName);
  if (index < 0) {
    return;
  }

  state.designer.flow.subFlows[index] = newName;
  state.designer.flow.selectedSubFlow = action.payload.navigateTo || 'main';
};

const setConversationDate: Action<TUpdateChatDate> = (state, action) => {
  const chat = state.chat.chatList.find(
    (obj) => obj.id === action.payload.chatId,
  );

  if (chat) {
    chat.date = action.payload.date;
  }
};

const setCreatedNewDesignerSubflow: Action<string> = (state, action) => {
  if (state.designer.blockUpdates.subFlowsCreated.length >= 0) {
    state.designer.blockUpdates.subFlowsCreated = [action.payload];
  } else {
    state.designer.blockUpdates.subFlowsCreated = [
      ...state.designer.blockUpdates.subFlowsCreated,
      action.payload,
    ];
  }
};

const setDeletedDesignerSubflow: Action<string> = (state, action) => {
  if (state.designer.blockUpdates.subFlowsDeleted.length >= 0) {
    state.designer.blockUpdates.subFlowsDeleted = [action.payload];
  } else {
    state.designer.blockUpdates.subFlowsDeleted = [
      ...state.designer.blockUpdates.subFlowsDeleted,
      action.payload,
    ];
  }
};

const setChatStatusForUser: Action<{
  chatId: string;
  activityState: TActivityChatState;
}> = (state, action) => {
  const chat = state.chat.chatList.find(
    (obj) => obj.id === action.payload.chatId,
  );

  if (chat) {
    chat.activityState = action.payload.activityState;
  }
};

const setEditRevisionContent: Action<RevisionDTO> = (state, action) => {
  state.designer.editRevisionContent = action.payload;
};

const setSelectedRevisionDetail: Action<RevisionDTO> = (state, action) => {
  state.designer.mainRevisionDetail = action.payload;
  // * Reset the selected subflow to main when a new revision is selected
  state.designer.flow.selectedSubFlow = 'main';
};

const clearSelectedRevisionDetail: Action = (state) => {
  return {
    ...state,
    designer: {
      ...state.designer,
      mainRevisionDetail: undefined,
    },
  };
};

const populateRevision: Action<RevisionDTO[]> = (state, action) => {
  const revisions = action.payload;
  state.designer.revisions = revisions;
  revisions?.map((item) => {
    if (item.isMainRevision) {
      state.designer.mainRevisionDetail = item;
    }
  });
};

const getRevisionsByMain: Action<{
  id: string;
  updateData: RevisionUpdateDTO;
}> = (state, action) => {
  const revisionSetFalse = (state.designer.revisions || []).map((item) => {
    return { ...item, isMainRevision: false };
  });

  state.designer.revisions = (revisionSetFalse || []).map((item) => {
    if (item._id === action.payload.id) {
      return { ...item, isMainRevision: true };
    }
    return item;
  });
};

const addRevision: Action<RevisionDTO> = (state, action) => {
  state.designer.revisions?.push(action.payload);
};

const deleteRevision: Action<string> = (state, action) => {
  state.designer.revisions = state.designer.revisions?.filter(
    (item) => item._id !== action.payload,
  );
};

const updateRevision: Action<{ id: string; updateData: RevisionUpdateDTO }> = (
  state,
  action,
) => {
  state.designer.revisions = state.designer.revisions?.map((item) => {
    if (item._id === action.payload.id) {
      return { ...item, name: action.payload.updateData.name };
    }
    return item;
  });
};

const setLoadingState: Action<boolean> = (state, action) => {
  state.ui.isLoading = action.payload;
};

const setDataSources: Action<DataSourceDetailsDTO[]> = (state, action) => {
  state.dataSources.files = action.payload;
};

const addDataSources: Action<DataSourceDetailsDTO[]> = (state, action) => {
  state.dataSources.files = [...action.payload, ...state.dataSources.files];
};

const setDataSource: Action<DataSourceDetailsDTO> = (state, action) => {
  state.dataSources.files = [action.payload, ...state.dataSources.files];
};

const setDataSourceFolders: Action<DataSourceFolderWithSubfoldersDTO[]> = (
  state,
  action,
) => {
  state.dataSources.folders = action.payload;
};

const setSelectedDataSourceFolder: Action<
  DataSourceFolderWithSubfoldersDTO | null
> = (state, action) => {
  state.dataSources.selectedFolder = action.payload || undefined;
};

const setSelectedDataSource: Action<DataSourceDetailsDTO> = (state, action) => {
  state.dataSources.selectedFile = action.payload;
};

const deleteDataSource: Action<string> = (state, action) => {
  state.dataSources.files = state.dataSources.files.filter(
    (file) => file._id?.toString() !== action.payload,
  );
};

const setDataSourceSearchString: Action<string> = (state, action) => {
  state.dataSources.search = action.payload;
};

const setLastTrainingActionAt: Action<string | null> = (state, action) => {
  state.dataSources.lastTrainingActionAt = action.payload;
};

const setSubflowTemplates: Action<SubflowTemplateDTO[]> = (state, action) => {
  state.designer.subflowTemplates.templates = action.payload;
};

const setSelectedSubflowTemplate: Action<SubflowTemplateDTO> = (
  state,
  action,
) => {
  state.designer.subflowTemplates.selectedTemplate = action.payload;
};

const deleteSubflowTemplate: Action<string> = (state, action) => {
  state.designer.subflowTemplates.templates =
    state.designer.subflowTemplates.templates.filter(
      (template: SubflowTemplateDTO) =>
        template._id.toString() !== action.payload,
    );
};

const setSubflowTemplateSearchString: Action<string> = (state, action) => {
  state.designer.subflowTemplates.search = action.payload;
};

const setPreviewConfigs: Action<PreviewConfigsDTO> = (state, action) => {
  state.preview = action.payload;
};

const setLLMUsages: Action<TLLMUsagesHistoryResponse> = (state, action) => {
  state.llmUsages = action.payload;
};

const resetStates: Action<{ shouldResetSelectedCompany: boolean }> = (
  state,
  action,
) => {
  state.chat = initialState.chat;
  state.settings = initialState.settings;
  state.designer = initialState.designer;
  state.activityLog = initialState.activityLog;
  state.gdpr = initialState.gdpr;
  state.exports = initialState.exports;
  state.preview = initialState.preview;
  state.llmUsages = initialState.llmUsages;
  // * Voice agents are company-scoped and the ElevenLabs widget mounts from the
  // * stored selection, so a company switch must not carry one over.
  state.voiceAi = initialState.voiceAi;

  if (
    action.payload.shouldResetSelectedCompany &&
    state.user.currentUser?.selectedCompanyId
  ) {
    state.user.currentUser.selectedCompanyId = undefined;
  }
};

const setDuplicateQuestionChecklist: Action<
  TConversationDetailsSchema[] | undefined
> = (state, action) => {
  state.duplicateQuestionChecklist = action.payload;
};

const setMetricsStartDate: Action<Date | null> = (state, action) => {
  const date = action.payload;
  const defaultStartDate = new Date(
    new Date().setDate(new Date().getDate() - 7),
  );
  state.metrics.startDate = date ? date : new Date(defaultStartDate);
};

const setMetricsEndDate: Action<Date | null> = (state, action) => {
  const date = action.payload;
  const defaultEndDate = new Date(new Date());
  state.metrics.endDate = date ? date : new Date(defaultEndDate);
};

const setMetricsData: Action<TPlatformReportingResponse> = (state, action) => {
  state.metrics.data = action.payload;
};

const setHotTopicConversationIds: Action<string[] | undefined> = (
  state,
  action,
) => {
  state.hotTopicConversationIds = action.payload;
};

const setTemplateResponses: Action<TemplateResponseDTO[]> = (state, action) => {
  state.templateResponses.templates = action.payload;
};

const setSelectedTemplateResponse: Action<TemplateResponseDTO | undefined> = (
  state,
  action,
) => {
  state.templateResponses.selectedTemplate = action.payload;
};

const deleteTemplateResponse: Action<string> = (state, action) => {
  state.templateResponses.templates = state.templateResponses.templates.filter(
    (template) => template._id.toString() !== action.payload,
  );
  // Clear selected template if it was deleted
  if (
    state.templateResponses.selectedTemplate?._id?.toString() === action.payload
  ) {
    state.templateResponses.selectedTemplate = undefined;
  }
};

const setConversationKeywords: Action<ConversationKeywordDTO[]> = (
  state,
  action,
) => {
  state.conversationKeywords.keywords = action.payload;
};

const setSelectedConversationKeyword: Action<
  ConversationKeywordDTO | undefined
> = (state, action) => {
  state.conversationKeywords.selectedKeyword = action.payload;
};

const updateConversationKeyword: Action<ConversationKeywordDTO> = (
  state,
  action,
) => {
  state.conversationKeywords.keywords = state.conversationKeywords.keywords.map(
    (keyword) =>
      keyword._id === action.payload._id ? action.payload : keyword,
  );
  // Keep selected keyword in sync if it was updated
  if (state.conversationKeywords.selectedKeyword?._id === action.payload._id) {
    state.conversationKeywords.selectedKeyword = action.payload;
  }
};

const deleteConversationKeyword: Action<string> = (state, action) => {
  state.conversationKeywords.keywords =
    state.conversationKeywords.keywords.filter(
      (keyword) => keyword._id !== action.payload,
    );
  // Clear selected keyword if it was deleted
  if (state.conversationKeywords.selectedKeyword?._id === action.payload) {
    state.conversationKeywords.selectedKeyword = undefined;
  }
};

const addMembers: Action<CompanyMemberDTO[]> = (state, action) => {
  const { payload: members } = action;

  state.settings.company = {
    ...state.settings.company,
    members: [...(state.settings.company?.members || []), ...members],
  };
};

const updateMemberCompanyIds: Action<{
  memberId: string;
  companyIds: string[];
}> = (state, action) => {
  const {
    payload: { memberId, companyIds },
  } = action;

  state.settings.company = {
    ...state.settings.company,
    members:
      state.settings.company?.members?.map((member) =>
        member.id === memberId ? { ...member, companyIds: companyIds } : member,
      ) || [],
  };
};

const setSelectedVoiceAIAgent: Action<string | undefined> = (state, action) => {
  state.voiceAi.selectedAgentId = action.payload;
};

const setVoiceAIAgents: Action<TSetVoiceAIAgentsPayload> = (state, action) => {
  const { agents, reason } = action.payload;

  state.voiceAi.agents = agents;
  state.voiceAi.reason = reason;
};

// * Clears the list and the selection, because the ElevenLabs widget mounts from
// * whatever `selectedAgentId` is left in the store: a company switch into a
// * failed refetch would otherwise hand the user the previous company's agent.
const failedVoiceAIAgentsFetch: Action = (state) => {
  state.voiceAi.agents = [];
  state.voiceAi.selectedAgentId = undefined;
  state.voiceAi.reason = VoiceAIAgentReason.FetchFailed;
};

const isLoading: Action = (state) => {
  // * Can be filled with anything, maybe add a call ID to each call to backend and put this here?
  state.ui.loadingStates.push(0);
  state.ui.isLoading = !!state.ui.loadingStates.length;
};

const finishedLoading: Action = (state) => {
  // * If we add call IDs as mentioned above we will need to filter by the specific ID
  state.ui.loadingStates.pop();
  state.ui.isLoading = !!state.ui.loadingStates.length;
};

export const mainSlice = createSlice({
  name: 'main',
  initialState,
  reducers: {
    setLogIn,
    setChatState,
    setUIState,
    setUserState,
    setSettingsState,
    setDesignerState,
    setColorState,
    setConversationAssigneeList,
    setConversationMessages,
    setSelectedConversation,
    setCompanySettings,
    addCompanyMember,
    updateCompanyPackages,
    updateCompanyPlatformFeatures,
    updateCompanyPlatformFeaturesMetadata,
    togglePermissionForRole,
    setRoles,
    setAssignableRoles,
    updateCurrentUser,
    updateBlocks,
    deleteBlocks,
    clearBlockUpdates,
    setLinkStatistics,
    setExportFiles,
    setSelectedExportFile,
    removeExport,
    setExportSearchString,
    setGDPRUsers,
    removeGDPRUser,
    toggleSelectedLanguage,
    setLanguages,
    setExportConfigStatus,
    removeExportConfig,
    addExportConfig,
    editExportConfig,
    setSelectedExportConfig,
    setSelectedGDPRUser,
    setActivityLogs,
    addChatNotifications,
    clearChatNotifications,
    setDebugConfig,
    setValidationConfig,
    setAvailabilities,
    setSelectedDate,
    setUserDataMapping,
    setCalendarFilter,
    setCalendarView,
    setMFAForCompany,
    setTimezone,
    setStreamingResponseConfig,
    setRestartPolicy,
    setWebclient,
    setUserProfileFields,
    setChatPlugin,
    setSubFlowToDisplay,
    setSubFlowsForFlow,
    addSubFlowForFlow,
    removeSubFlowFromFlow,
    updateSubFlow,
    setConversationDate,
    setCreatedNewDesignerSubflow,
    setDeletedDesignerSubflow,
    setChatStatusForUser,
    addRevision,
    deleteRevision,
    setEditRevisionContent,
    setSelectedRevisionDetail,
    clearSelectedRevisionDetail,
    updateRevision,
    populateRevision,
    getRevisionsByMain,
    setLoadingState,
    setDataSources,
    addDataSources,
    setDataSource,
    setSelectedDataSource,
    deleteDataSource,
    setDataSourceSearchString,
    setLastTrainingActionAt,
    setSubflowTemplates,
    setSelectedSubflowTemplate,
    deleteSubflowTemplate,
    setSubflowTemplateSearchString,
    setPreviewConfigs,
    setIsEnabledWebclientAccess,
    resetStates,
    setLLMUsages,
    setDuplicateQuestionChecklist,
    setMetricsStartDate,
    setMetricsEndDate,
    setMetricsData,
    setHotTopicConversationIds,
    setTemplateResponses,
    setSelectedTemplateResponse,
    deleteTemplateResponse,
    setConversationKeywords,
    setSelectedConversationKeyword,
    updateConversationKeyword,
    deleteConversationKeyword,
    addMembers,
    updateMemberCompanyIds,
    setSelectedVoiceAIAgent,
    setVoiceAIAgents,
    failedVoiceAIAgentsFetch,
    isLoading,
    finishedLoading,
    setDataSourceFolders,
    setSelectedDataSourceFolder,
  },
});
export const mainReducer = mainSlice.reducer;
export const mainActions = mainSlice.actions;

// TODO: Refactor project-wide so we use sliceActions rather than calling useMainDispatch
export const mainHandler = {
  [Actions.SET_LOGIN]: mainActions.setLogIn,
  [Actions.SET_CHAT]: mainActions.setChatState,
  [Actions.SET_UI]: mainActions.setUIState,
  [Actions.SET_USER]: mainActions.setUserState,
  [Actions.SET_SETTINGS]: mainActions.setSettingsState,
  [Actions.SET_DESIGNER]: mainActions.setDesignerState,
  [Actions.SET_COLOR]: mainActions.setColorState,
  [Actions.SET_CONVERSATION_ASSIGNEE_LIST]:
    mainActions.setConversationAssigneeList,
  [Actions.SET_CHAT_MESSAGES]: mainActions.setConversationMessages,
  [Actions.SET_SELECTED_CONVERSATION]: mainActions.setSelectedConversation,
  [Actions.SET_COMPANY_SETTINGS]: mainActions.setCompanySettings,
  [Actions.ADD_COMPANY_MEMBER]: mainActions.addCompanyMember,
  [Actions.UPDATE_COMPANY_PACKAGES_SUCCESS]: mainActions.updateCompanyPackages,
  [Actions.UPDATE_COMPANY_PLATFORM_FEATURES_SUCCESS]:
    mainActions.updateCompanyPlatformFeatures,
  [Actions.UPDATE_COMPANY_PLATFORM_FEATURES_METADATA_SUCCESS]:
    mainActions.updateCompanyPlatformFeaturesMetadata,
  [Actions.TOGGLE_PERMISSION_FOR_ROLE]: mainActions.togglePermissionForRole,
  [Actions.SET_ROLES]: mainActions.setRoles,
  [Actions.SET_ASSIGNABLE_ROLES]: mainActions.setAssignableRoles,
  [Actions.UPDATE_CURRENT_USER]: mainActions.updateCurrentUser,
  [Actions.UPDATE_BLOCKS]: mainActions.updateBlocks,
  [Actions.DELETE_BLOCKS]: mainActions.deleteBlocks,
  [Actions.BLOCK_UPDATES_CLEAR]: mainActions.clearBlockUpdates,
  [Actions.SET_LINK_STATISTICS]: mainActions.setLinkStatistics,
  [Actions.SET_EXPORTS]: mainActions.setExportFiles,
  [Actions.SET_EXPORT_FILE]: mainActions.setSelectedExportFile,
  [Actions.REMOVE_EXPORT]: mainActions.removeExport,
  [Actions.SET_GDPR_USERS]: mainActions.setGDPRUsers,
  [Actions.REMOVE_GDPR_USER]: mainActions.removeGDPRUser,
  [Actions.TOGGLE_SELECTED_LANGUAGE]: mainActions.toggleSelectedLanguage,
  [Actions.SET_LANGUAGES]: mainActions.setLanguages,
  [Actions.SET_EXPORT_CONFIG_STATUS]: mainActions.setExportConfigStatus,
  [Actions.REMOVE_EXPORT_CONFIG]: mainActions.removeExportConfig,
  [Actions.ADD_EXPORT_CONFIG]: mainActions.addExportConfig,
  [Actions.EDIT_EXPORT_CONFIG]: mainActions.editExportConfig,
  [Actions.SET_SELECTED_EXPORT_CONFIG]: mainActions.setSelectedExportConfig,
  [Actions.SET_GDPR_USER]: mainActions.setSelectedGDPRUser,
  [Actions.SET_ACTIVITY_LOGS]: mainActions.setActivityLogs,
  [Actions.ADD_CHAT_NOTIFICATIONS]: mainActions.addChatNotifications,
  [Actions.CLEAR_CHAT_NOTIFICATIONS]: mainActions.clearChatNotifications,
  [Actions.SET_DEBUG_CONFIG]: mainActions.setDebugConfig,
  [Actions.SET_VALIDATION_CONFIG]: mainActions.setValidationConfig,
  [Actions.SET_AVAILABILITIES]: mainActions.setAvailabilities,
  [Actions.SET_SELECTED_DATE]: mainActions.setSelectedDate,
  [Actions.SET_USER_DATA_MAPPING]: mainActions.setUserDataMapping,
  [Actions.SET_CALENDAR_FILTER]: mainActions.setCalendarFilter,
  [Actions.SET_CALENDAR_VIEW]: mainActions.setCalendarView,
  [Actions.SET_MFA_FOR_COMPANY]: mainActions.setMFAForCompany,
  [Actions.SET_TIMEZONE]: mainActions.setTimezone,
  [Actions.SET_STREAMING_RESPONSE_CONFIG]:
    mainActions.setStreamingResponseConfig,
  [Actions.SET_RESTART_POLICY]: mainActions.setRestartPolicy,
  [Actions.SET_WEBCLIENT]: mainActions.setWebclient,
  [Actions.SET_USER_PROFILE_FIELDS]: mainActions.setUserProfileFields,
  [Actions.SET_CHAT_PLUGIN]: mainActions.setChatPlugin,
  [Actions.SET_CONVERSATION_DATE]: mainActions.setConversationDate,
  [Actions.SET_CREATED_NEW_DESIGNER_SUBFLOW]:
    mainActions.setCreatedNewDesignerSubflow,
  [Actions.SET_DELETED_DESIGNER_SUBFLOW]: mainActions.setDeletedDesignerSubflow,
};
